/**
 * Reglas de la carga a mano de un canje desde la bandeja (0374). Módulo PURO, como `canjes.ts`.
 *
 * Reusa las reglas del celular (premios sugeridos, equivalencia, tope por cliente) en vez de
 * copiarlas: un canje cargado en la oficina tiene que quedar igual que uno registrado en la
 * tienda, o el tablero del cliente contaría distinto según quién lo cargó.
 *
 * Dos diferencias con el celular, y las dos son a propósito:
 * - **El nombre del cliente nunca es obligatorio.** Se regulariza desde una planilla en papel que
 *   muchas veces solo trae cantidades; exigirlo obligaría a inventarlo. La 0030 ya permite el NULL.
 * - **El motivo SÍ es obligatorio.** Es lo que separa una regularización de un error, y el
 *   servidor rechaza la carga sin él.
 */
import {
  aplicarPremioPorTotal, esEquivalencia, excedeTope, faltaPremio, premiosSugeridos,
  type DinamicaConfig, type LineaCanje, type PremioEntregado, type ReglaPremioTotal,
} from './canjes';

/** Perú no tiene horario de verano: el offset es -05:00 todo el año. */
const LIMA_OFFSET = '-05:00';

/** Una línea en edición. `premiosManuales` en `null` = se usan los sugeridos por la dinámica. */
export interface LineaManual {
  dynamicId: string;
  canjes: number;
  premiosManuales: PremioEntregado[] | null;
}

export const lineaManualVacia = (): LineaManual => ({ dynamicId: '', canjes: 1, premiosManuales: null });

/**
 * Las líneas con dinámica elegida y sus premios resueltos.
 *
 * Una dinámica que ya no está en la configuración (se cambió la fecha y dejó de estar vigente) se
 * descarta acá en vez de viajar: el servidor la aceptaría —es de la campaña— y el canje quedaría
 * marcado fuera de dinámica sin que nadie lo haya pedido.
 */
export function resolverLineas(
  lineas: LineaManual[], dinamicas: DinamicaConfig[], regla: ReglaPremioTotal | null = null,
): LineaCanje[] {
  const porId = new Map(dinamicas.map((d) => [d.id, d]));
  const base = lineas.flatMap((l) => {
    const d = porId.get(l.dynamicId);
    if (!d) return [];
    return [{
      dynamicId: l.dynamicId,
      canjes: l.canjes,
      premios: (l.premiosManuales ?? premiosSugeridos(d, l.canjes)).filter((p) => p.units > 0),
    }];
  });
  // Con regla de premio por total (0386) el premio NO sale de cada línea: se calcula del canje entero. Con
  // combinaciones (0407) cada línea suma a la bolsa de su dinámica.
  return regla === null ? base : aplicarPremioPorTotal(base, regla, (id) => porId.get(id)?.grupo);
}

export interface EstadoCargaManual {
  campaignId: string;
  fecha: string;
  /** Hoy en Lima, `yyyy-MM-dd`. Llega de afuera para que esta función no lea el reloj. */
  hoy: string;
  pdvId: string;
  workerId: string;
  lineas: LineaManual[];
  dinamicas: DinamicaConfig[];
  maxPerClient: number | null;
  motivo: string;
  /** Premio por el total del canje (0386). Con ella no hay premio que preguntar por línea. */
  regla?: ReglaPremioTotal | null;
}

/**
 * Por qué NO se puede guardar todavía, o `null` si sí se puede.
 *
 * Devuelve el texto para ponerlo junto al botón apagado: enterarse después de pulsar es enterarse
 * tarde (la misma regla que `bloqueoDeCorreccion`). El orden es el de la pantalla, de arriba
 * abajo, para que el aviso siempre señale el primer campo que falta.
 */
export function bloqueoDeCargaManual(e: EstadoCargaManual): string | null {
  if (e.campaignId === '') return 'Elige la campaña.';
  if (e.fecha === '') return 'Elige la fecha del canje.';
  if (e.fecha > e.hoy) return 'La fecha es futura: un canje se carga cuando ya ocurrió.';
  if (e.pdvId === '') return 'Elige la tienda.';
  if (e.workerId === '') return 'Elige a la persona que hizo el canje.';

  const porId = new Map(e.dinamicas.map((d) => [d.id, d]));
  const regla = e.regla ?? null;
  const resueltas = resolverLineas(e.lineas, e.dinamicas, regla);
  if (resueltas.length === 0) return 'Elige al menos una dinámica.';

  // `canje_lines` tiene UNIQUE (record_id, dynamic_id): dos líneas de la misma dinámica harían
  // rebotar el canje entero con un error de clave duplicada que no dice qué hacer.
  const vistas = new Set<string>();
  for (const l of resueltas) {
    if (vistas.has(l.dynamicId)) {
      return `Repetiste «${porId.get(l.dynamicId)?.name ?? 'una dinámica'}»: suma sus canjes en una `
        + 'sola línea.';
    }
    vistas.add(l.dynamicId);
  }

  if (resueltas.some((l) => !Number.isInteger(l.canjes) || l.canjes < 1)) {
    return 'Cada dinámica necesita al menos 1 canje, en número entero.';
  }
  // Con regla por total, un canje de 1 o 2 tiras va SIN premio y es válido: se registra la venta igual.
  if (regla === null) {
    const sinPremio = faltaPremio(resueltas, e.dinamicas);
    if (sinPremio !== null) return sinPremio;
    const vacia = resueltas.find((l) => l.premios.length === 0);
    if (vacia !== undefined) {
      return `Falta el premio entregado en «${porId.get(vacia.dynamicId)?.name ?? 'una dinámica'}».`;
    }
  }
  if (excedeTope(resueltas, e.maxPerClient)) {
    const usados = resueltas.reduce((s, l) => s + l.canjes, 0);
    return `La campaña permite hasta ${e.maxPerClient} canjes por cliente y este lleva ${usados}: `
      + 'carga el resto como otro canje.';
  }
  if (e.motivo.trim() === '') return 'Escribe el motivo de la carga.';
  return null;
}

/** Las líneas en la forma que guarda el servidor, con la equivalencia CALCULADA (no preguntada). */
export function lineasParaGuardar(
  resueltas: LineaCanje[], dinamicas: DinamicaConfig[], regla: ReglaPremioTotal | null = null,
): { dynamicId: string; canjes: number; premios: { prizeId: string; units: number; equivalencia: boolean }[] }[] {
  const porId = new Map(dinamicas.map((d) => [d.id, d]));
  return resueltas.map((l) => {
    const d = porId.get(l.dynamicId);
    // Con regla por total el premio lo calculó el sistema: compararlo con lo «esperado» de la dinámica (vacío) lo
    // marcaría como equivalencia por falta de stock en TODOS los canjes.
    const equivalencia = regla === null && d ? esEquivalencia(l, d) : false;
    return {
      dynamicId: l.dynamicId,
      canjes: l.canjes,
      premios: l.premios.map((p) => ({ prizeId: p.prizeId, units: p.units, equivalencia })),
    };
  });
}

/**
 * El momento declarado, si se sabe la hora. Sin hora va `null`: inventar mediodía fabricaría un
 * pico en cualquier curva por hora (la lección del canje manual de Vital, `hora_real`).
 */
export function ocurridoEnDesde(fecha: string, hora: string): string | null {
  if (fecha === '' || hora === '') return null;
  return `${fecha}T${hora}:00${LIMA_OFFSET}`;
}
